/*
 * KAYSER VISITOR INTELLIGENCE — STOREFRONT EVENT LAYER
 * Version 0.2.21 / schema 1.0.0
 *
 * This file publishes namespaced custom events into Shopify's analytics bus.
 * It reads no form values and sets no cookies. Consent-bound custom events
 * use both Shopify's analytics bus and a first-party browser transport. Both
 * transports share event identities, so the collector deduplicates them.
 */
(function () {
  'use strict';

  var VERSION = '0.2.21';
  var SCHEMA_VERSION = '1.0.0';
  var NAMESPACE = 'kfa:';
  var context = readContext();
  var queue = [];
  var publishAttempts = 0;
  var maxPublishAttempts = 480;
  var publishRetryTimer = null;
  var impressionTimers = new WeakMap();
  var observedElements = new WeakSet();
  var fired = new Set();
  var lastActivityAt = Date.now();
  var activeSeconds = 0;
  var HEATMAP_COLUMNS = 24;
  var HEATMAP_ROWS = 48;
  var POINTER_SAMPLE_INTERVAL = 200;
  var HEATMAP_FLUSH_INTERVAL = 1000;
  var pointerCells = new Map();
  var lastPointerSampleAt = 0;
  var heatmapFlushTimer = null;
  var lastPointerType = 'mouse';
  var debugPanel = null;
  var debugClickCount = 0;
  var debugPointerCount = 0;
  var DEBUG_COUNTERS_KEY = 'kfa_intelligence_debug_counts_v1';
  var DEBUG_RUN_KEY = 'kfa_intelligence_debug_run_v1';
  var DEBUG_SERVER_CACHE_KEY = 'kfa_intelligence_debug_server_v2';
  var DEBUG_STATUS_ENDPOINT = 'https://www.frankkayser.com/visitor-intelligence/api/test-status.php';
  var debugRunId = '';
  var debugServerStatus = null;
  var debugServerReachable = false;
  var debugServerEmptyPolls = 0;
  var debugServerConsecutiveFailures = 0;
  var debugServerPollTimer = null;
  var lastMousePointerDownTarget = null;
  var lastMousePointerDownAt = 0;
  var DIRECT_ENDPOINT = 'https://www.frankkayser.com/visitor-intelligence/v1/events.php';
  var DIRECT_BROWSER_KEY = '3ad6975c4b631191f83e01f8e23a5eceae275b7b7925e07d20f19f10103de2ab';
  var DIRECT_EPISODE_KEY = 'kfa_intelligence_episode_v1';
  var DIRECT_PENDING_KEY = 'kfa_intelligence_pending_v1';
  var FIRST_PARTY_CONSENT_KEY = 'kfa_consent_choice_v1';
  var directEpisodeId = '';
  var directQueue = [];
  var directPending = new Map();
  var directActive = 0;
  var directConsentTimer = null;
  var directConsentChecks = 0;
  var directConsentLoading = false;
  var directConsentLoadAttempts = 0;
  var DIRECT_MAX_QUEUE = 500;
  var DIRECT_MAX_ACTIVE = 4;

  function debugMode() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.has('kfa_intelligence_debug')) {
        if (params.get('kfa_intelligence_debug') === 'reset') {
          window.sessionStorage.removeItem(DEBUG_COUNTERS_KEY);
          window.sessionStorage.removeItem(DEBUG_SERVER_CACHE_KEY);
        }
        window.sessionStorage.setItem('kfa_intelligence_debug', '1');
        return true;
      }
      if (String(context.theme_id || '') === '202765664590') return true;
      return window.sessionStorage.getItem('kfa_intelligence_debug') === '1';
    } catch (error) {
      return false;
    }
  }

  function readDebugCounters() {
    try {
      var saved = JSON.parse(window.sessionStorage.getItem(DEBUG_COUNTERS_KEY) || '{}');
      debugClickCount = Math.max(0, Number(saved.clicks) || 0);
      debugPointerCount = Math.max(0, Number(saved.pointerSamples) || 0);
    } catch (error) {
      debugClickCount = 0;
      debugPointerCount = 0;
    }
  }

  function persistDebugCounters() {
    if (!debugMode()) return;
    try {
      window.sessionStorage.setItem(DEBUG_COUNTERS_KEY, JSON.stringify({
        clicks: debugClickCount,
        pointerSamples: debugPointerCount
      }));
    } catch (error) {
      // The diagnostic display must never interfere with storefront behavior.
    }
  }

  function generateDebugRunId() {
    var bytes = new Uint8Array(16);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(bytes);
    } else {
      for (var index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }
    return 'kfa-' + Array.from(bytes).map(function (value) {
      return value.toString(16).padStart(2, '0');
    }).join('');
  }

  function randomHex(bytesLength) {
    var bytes = new Uint8Array(bytesLength);
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      window.crypto.getRandomValues(bytes);
    } else {
      for (var index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }
    return Array.from(bytes).map(function (value) {
      return value.toString(16).padStart(2, '0');
    }).join('');
  }

  function transportEpisodeId() {
    if (directEpisodeId) return directEpisodeId;
    try {
      directEpisodeId = window.sessionStorage.getItem(DIRECT_EPISODE_KEY) || '';
      if (!/^kfa-session-[a-f0-9]{32}$/.test(directEpisodeId)) {
        directEpisodeId = 'kfa-session-' + randomHex(16);
        window.sessionStorage.setItem(DIRECT_EPISODE_KEY, directEpisodeId);
      }
    } catch (error) {
      directEpisodeId = 'kfa-session-' + randomHex(16);
    }
    return directEpisodeId;
  }

  function analyticsConsentState() {
    var firstPartyChoice = '';
    try {
      firstPartyChoice = window.localStorage.getItem(FIRST_PARTY_CONSENT_KEY) || '';
    } catch (error) {}
    if (firstPartyChoice !== 'analytics' && firstPartyChoice !== 'essential') {
      try {
        var consentMatch = document.cookie.match(new RegExp('(?:^|;\\s*)' + FIRST_PARTY_CONSENT_KEY + '=([^;]*)'));
        firstPartyChoice = consentMatch ? decodeURIComponent(consentMatch[1]) : '';
      } catch (error) {
        firstPartyChoice = '';
      }
    }
    if (firstPartyChoice === 'analytics') return true;
    if (firstPartyChoice === 'essential') return false;

    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api) return null;
    try {
      if (typeof api.analyticsProcessingAllowed === 'function') {
        return Boolean(api.analyticsProcessingAllowed());
      }
      if (typeof api.getTrackingConsent === 'function') {
        var consent = api.getTrackingConsent();
        var analytics = consent && typeof consent === 'object' ? consent.analytics : consent;
        if (analytics === true || analytics === 'yes' || analytics === 'granted' || analytics === 'accepted') return true;
        if (analytics === false || analytics === 'no' || analytics === 'denied' || analytics === 'declined') return false;
      }
      if (typeof api.userCanBeTracked === 'function') return Boolean(api.userCanBeTracked());
    } catch (error) {}
    return null;
  }

  function directConfigured() {
    return DIRECT_BROWSER_KEY && DIRECT_BROWSER_KEY.indexOf('REPLACE_') !== 0;
  }

  function directEnvelope(item) {
    return {
      schema_version: SCHEMA_VERSION,
      layer_version: VERSION,
      source: 'shopify_theme',
      event_kind: 'custom',
      event_id: item.payload.transport_event_id,
      episode_id: item.payload.transport_episode_id,
      event_name: item.name,
      occurred_at: item.payload.occurred_at,
      page_path: item.payload.page_path,
      payload: item.payload
    };
  }

  function persistDirectPending() {
    try {
      var pending = Array.from(directPending.values()).slice(-100);
      if (pending.length) {
        window.sessionStorage.setItem(DIRECT_PENDING_KEY, JSON.stringify(pending));
      } else {
        window.sessionStorage.removeItem(DIRECT_PENDING_KEY);
      }
    } catch (error) {}
  }

  function hydrateDirectPending() {
    try {
      var pending = JSON.parse(window.sessionStorage.getItem(DIRECT_PENDING_KEY) || '[]');
      if (!Array.isArray(pending)) return;
      pending.slice(-100).forEach(function (item) {
        var eventId = item && item.payload && item.payload.transport_event_id;
        if (!eventId || !item.name) return;
        directPending.set(eventId, item);
        directQueue.push({ item: item, attempt: 1 });
      });
    } catch (error) {
      try { window.sessionStorage.removeItem(DIRECT_PENDING_KEY); } catch (storageError) {}
    }
  }

  function directFinish(item) {
    var eventId = item && item.payload && item.payload.transport_event_id;
    if (eventId) directPending.delete(eventId);
    persistDirectPending();
    directActive = Math.max(0, directActive - 1);
    drainDirectQueue();
  }

  function noteDirectDebugAck(item) {
    if (!debugMode() || !item || !item.name) return;
    var clickIncrement = item.name === 'kfa:click_point' ? 1 : 0;
    var pointerIncrement = item.name === 'kfa:pointer_density'
      ? Math.max(0, Number(item.payload && item.payload.sample_count) || 0)
      : 0;
    if (!clickIncrement && !pointerIncrement) return;

    if (!debugServerStatus) {
      debugServerStatus = { click_points: 0, pointer_samples: 0 };
    }
    debugServerStatus.click_points += clickIncrement;
    debugServerStatus.pointer_samples += pointerIncrement;
    debugServerReachable = true;
    debugServerConsecutiveFailures = 0;
    debugServerEmptyPolls = 0;
    persistDebugServerCache();
    updateDebugPanel();
  }

  function directDeliver(item, attempt) {
    if (!item.payload.transport_episode_id) {
      item.payload.transport_episode_id = transportEpisodeId();
      directPending.set(item.payload.transport_event_id, item);
      persistDirectPending();
    }
    var body = JSON.stringify(directEnvelope(item));
    directActive += 1;
    window.fetch(DIRECT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-KFA-Browser-Key': DIRECT_BROWSER_KEY
      },
      body: body,
      keepalive: true,
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    }).then(function (response) {
      if (response.ok) return;
      if (response.status < 500 && response.status !== 429) return;
      throw new Error('direct_' + response.status);
    }).then(function () {
      noteDirectDebugAck(item);
      directFinish(item);
    }).catch(function () {
      directActive = Math.max(0, directActive - 1);
      if (attempt < 8) {
        window.setTimeout(function () {
          directQueue.unshift({ item: item, attempt: attempt + 1 });
          drainDirectQueue();
        }, Math.min(10000, 250 * Math.pow(2, attempt)));
      } else {
        // Keep the unconfirmed event in sessionStorage. A following page load
        // rehydrates it and retries with the same event id; the collector's
        // INSERT IGNORE makes that retry safe if the earlier request arrived
        // but its response was lost.
        persistDirectPending();
        drainDirectQueue();
      }
    });
  }

  function drainDirectQueue() {
    if (!directConfigured()) {
      directQueue.length = 0;
      directPending.clear();
      persistDirectPending();
      return;
    }
    var consent = analyticsConsentState();
    if (consent === false) {
      directQueue.length = 0;
      directPending.clear();
      persistDirectPending();
      return;
    }
    if (consent !== true) {
      ensureDirectConsentApi();
      scheduleDirectConsentCheck();
      return;
    }
    while (directQueue.length && directActive < DIRECT_MAX_ACTIVE) {
      var delivery = directQueue.shift();
      directDeliver(delivery.item, delivery.attempt);
    }
  }

  function ensureDirectConsentApi() {
    if (window.Shopify && window.Shopify.customerPrivacy) return;
    if (directConsentLoading || directConsentLoadAttempts >= 10) return;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

    directConsentLoading = true;
    directConsentLoadAttempts += 1;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {
      directConsentLoading = false;
      drainDirectQueue();
      updateDebugPanel();
    });
  }

  function scheduleDirectConsentCheck() {
    if (directConsentTimer || directConsentChecks >= 300) return;
    directConsentChecks += 1;
    directConsentTimer = window.setTimeout(function () {
      directConsentTimer = null;
      drainDirectQueue();
    }, 100);
  }

  function enqueueDirect(item) {
    if (!directConfigured()) return;
    if (directQueue.length + directActive >= DIRECT_MAX_QUEUE) {
      if (directQueue.length) directQueue.shift();
      else return;
    }
    directQueue.push({ item: item, attempt: 1 });
    directPending.set(item.payload.transport_event_id, item);
    persistDirectPending();
    drainDirectQueue();
  }

  function initializeDebugRun() {
    try {
      var params = new URLSearchParams(window.location.search);
      var shouldReset = params.get('kfa_intelligence_debug') === 'reset';
      debugRunId = shouldReset ? '' : (window.sessionStorage.getItem(DEBUG_RUN_KEY) || '');
      if (!debugRunId) {
        debugRunId = generateDebugRunId();
        window.sessionStorage.setItem(DEBUG_RUN_KEY, debugRunId);
      }
      if (shouldReset) {
        params.set('kfa_intelligence_debug', '1');
        var replacement = window.location.pathname + '?' + params.toString() + window.location.hash;
        window.history.replaceState(window.history.state, '', replacement);
      }
    } catch (error) {
      debugRunId = generateDebugRunId();
    }
  }

  function readDebugServerCache() {
    try {
      var cached = JSON.parse(window.sessionStorage.getItem(DEBUG_SERVER_CACHE_KEY) || '{}');
      var fresh = Date.now() - Number(cached.saved_at || 0) < 30 * 60 * 1000;
      if (!fresh || cached.run_id !== debugRunId || !cached.status) return;
      debugServerStatus = {
        click_points: Math.max(0, Number(cached.status.click_points) || 0),
        pointer_samples: Math.max(0, Number(cached.status.pointer_samples) || 0)
      };
      debugServerReachable = true;
    } catch (error) {
      // A missing diagnostic cache must never interfere with the storefront.
    }
  }

  function persistDebugServerCache() {
    if (!debugServerStatus) return;
    try {
      window.sessionStorage.setItem(DEBUG_SERVER_CACHE_KEY, JSON.stringify({
        run_id: debugRunId,
        saved_at: Date.now(),
        status: debugServerStatus
      }));
    } catch (error) {
      // The diagnostic display must never interfere with storefront behavior.
    }
  }

  function requestDebugRecovery() {
    if (!debugPanel) return;
    if (analyticsConsentState() === true && debugServerStatus) return;

    if (!debugServerReachable) {
      debugServerConsecutiveFailures = 0;
      updateDebugPanel();
      pollDebugServerStatus();
      return;
    }

    var applyConsent = function () {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (!api || typeof api.setTrackingConsent !== 'function') return;
      debugPanel.textContent = 'KAYSER VI ' + VERSION + ' · SERVER WIRD AKTIVIERT …';
      api.setTrackingConsent({ analytics: true, marketing: false, preferences: false }, function () {
        try {
          window.sessionStorage.removeItem(DEBUG_COUNTERS_KEY);
          window.sessionStorage.removeItem(DEBUG_SERVER_CACHE_KEY);
          window.sessionStorage.removeItem(DEBUG_RUN_KEY);
        } catch (error) {}
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('kfa_intelligence_debug', 'reset');
          window.location.assign(url.href);
        } catch (error) {
          window.location.reload();
        }
      });
    };

    if (window.Shopify && window.Shopify.customerPrivacy) {
      applyConsent();
      return;
    }

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) applyConsent();
      });
    }
  }

  function updateDebugPanel() {
    if (!debugPanel) return;
    var serverText = 'SERVER PRÜFT';
    var background = 'rgba(55,55,55,.96)';
    var interactive = false;
    var consent = analyticsConsentState();
    var serverClicks = debugServerStatus ? Number(debugServerStatus.click_points || 0) : 0;
    var serverPointers = debugServerStatus ? Number(debugServerStatus.pointer_samples || 0) : 0;
    var serverCaughtUp = Boolean(debugServerStatus)
      && serverClicks >= debugClickCount
      && serverPointers >= debugPointerCount;

    if (consent === false) {
      serverText = 'MESSUNG AUS — HIER AKTIVIEREN';
      background = 'rgba(166,31,31,.96)';
      interactive = true;
    } else if (serverCaughtUp) {
      serverText = 'SERVER OK K ' + serverClicks + ' M ' + serverPointers;
      background = 'rgba(16,120,70,.96)';
    } else if (debugServerStatus) {
      serverText = 'SERVER ÜBERTRÄGT K ' + serverClicks + '/' + debugClickCount +
        ' M ' + serverPointers + '/' + debugPointerCount;
    } else if (
      debugServerReachable &&
      (debugClickCount > 0 || debugPointerCount > 0) &&
      debugServerEmptyPolls >= 30
    ) {
      serverText = 'KEINE MESSDATEN — HIER AKTIVIEREN';
      background = 'rgba(166,31,31,.96)';
      interactive = true;
    } else if (debugServerReachable && (debugClickCount > 0 || debugPointerCount > 0)) {
      serverText = 'SERVER WARTET AUF ÜBERTRAGUNG';
    } else if (debugServerReachable) {
      serverText = 'TEST BEREIT · BITTE KLICKEN';
    } else if (debugServerConsecutiveFailures >= 15) {
      serverText = 'STATUS NICHT ERREICHBAR — HIER NEU PRÜFEN';
      background = 'rgba(166,31,31,.96)';
      interactive = true;
    }

    debugPanel.textContent = 'KAYSER VI ' + VERSION + ' · LOKAL K ' + debugClickCount +
      ' M ' + debugPointerCount + ' · ' + serverText;
    debugPanel.style.background = background;
    debugPanel.style.pointerEvents = interactive ? 'auto' : 'none';
    debugPanel.style.cursor = interactive ? 'pointer' : 'default';
  }

  function pollDebugServerStatus() {
    if (!debugRunId || typeof window.fetch !== 'function') return;

    window.fetch(DEBUG_STATUS_ENDPOINT + '?run_id=' + encodeURIComponent(debugRunId), {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    }).then(function (response) {
      if (!response.ok) throw new Error('debug_status_' + response.status);
      return response.json();
    }).then(function (status) {
      debugServerReachable = true;
      debugServerConsecutiveFailures = 0;
      if (status && Number(status.custom_events) > 0) {
        debugServerStatus = {
          click_points: Math.max(0, Number(status.click_points) || 0),
          pointer_samples: Math.max(0, Number(status.pointer_samples) || 0)
        };
        debugServerEmptyPolls = 0;
        persistDebugServerCache();
      } else if (!debugServerStatus) {
        debugServerEmptyPolls += 1;
      }
      updateDebugPanel();
    }).catch(function () {
      debugServerConsecutiveFailures += 1;
      updateDebugPanel();
    });
  }

  function initializeDebugPanel() {
    if (!debugMode() || document.getElementById('kfa-intelligence-debug-panel')) return;
    readDebugCounters();
    initializeDebugRun();
    readDebugServerCache();
    debugPanel = document.createElement('div');
    debugPanel.id = 'kfa-intelligence-debug-panel';
    debugPanel.setAttribute('role', 'status');
    debugPanel.setAttribute('data-kfa-no-track', '');
    debugPanel.style.cssText = [
      'position:fixed',
      'top:10px',
      'right:10px',
      'z-index:2147483647',
      'padding:8px 10px',
      'background:rgba(55,55,55,.96)',
      'color:#fff',
      'font:600 11px/1.2 system-ui,sans-serif',
      'letter-spacing:.03em',
      'box-shadow:0 2px 10px rgba(0,0,0,.2)',
      'pointer-events:none'
    ].join(';');
    debugPanel.addEventListener('click', requestDebugRecovery);
    document.body.appendChild(debugPanel);
    updateDebugPanel();
    // Successful first-party ingest responses drive the status display. This
    // avoids cross-origin diagnostic polling, which Safari/WebKit can classify
    // as tracking even though the actual first-party event delivery succeeds.
  }

  function readContext() {
    var node = document.getElementById('kfa-intelligence-context');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function cleanToken(value, maxLength) {
    if (value === null || value === undefined) return '';
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9äöüß._:/-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, maxLength || 100);
  }

  function campaignContext() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return {};
    }

    var allowed = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'campaign_act',
      'campaign_module',
      'email_number'
    ];
    var result = {};
    allowed.forEach(function (key) {
      var value = cleanToken(params.get(key), 100);
      if (value) result[key] = value;
    });
    return result;
  }

  function deviceCategory() {
    var viewportWidth = Math.round(window.innerWidth || (document.documentElement && document.documentElement.clientWidth) || 0);
    if (viewportWidth > 0 && viewportWidth <= 749) return 'mobile';
    if (viewportWidth > 0 && viewportWidth <= 1024) return 'tablet';
    if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return 'mobile';
    if (window.matchMedia && window.matchMedia('(max-width: 1024px)').matches) return 'tablet';
    return 'desktop';
  }

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function roundRatio(value) {
    return Number(clamp(value, 0, 0.9999).toFixed(4));
  }

  function heatmapPageAllowed() {
    var path = String(window.location.pathname || '/').toLowerCase();
    return !/^\/(account|checkout|checkouts)(\/|$)/.test(path);
  }

  function sensitiveTarget(target) {
    if (!target || !target.closest) return true;
    return Boolean(target.closest(
      'input, textarea, select, option, form, [contenteditable], [data-kfa-no-track], [autocomplete]'
    ));
  }

  function documentGeometry() {
    var root = document.documentElement;
    var body = document.body;
    var width = Math.max(
      1,
      root ? root.scrollWidth : 0,
      root ? root.clientWidth : 0,
      body ? body.scrollWidth : 0
    );
    var height = Math.max(
      1,
      root ? root.scrollHeight : 0,
      root ? root.clientHeight : 0,
      body ? body.scrollHeight : 0
    );
    return { width: Math.round(width), height: Math.round(height) };
  }

  function heatmapCoordinates(event) {
    var geometry = documentGeometry();
    var pageX = Number.isFinite(event.pageX) ? event.pageX : Number(event.clientX || 0) + window.scrollX;
    var pageY = Number.isFinite(event.pageY) ? event.pageY : Number(event.clientY || 0) + window.scrollY;
    var xRatio = roundRatio(pageX / geometry.width);
    var yRatio = roundRatio(pageY / geometry.height);
    return {
      x_ratio: xRatio,
      y_ratio: yRatio,
      grid_col: Math.min(HEATMAP_COLUMNS - 1, Math.floor(xRatio * HEATMAP_COLUMNS)),
      grid_row: Math.min(HEATMAP_ROWS - 1, Math.floor(yRatio * HEATMAP_ROWS)),
      grid_columns: HEATMAP_COLUMNS,
      grid_rows: HEATMAP_ROWS,
      document_width: geometry.width,
      document_height: geometry.height
    };
  }

  function layoutKey(geometry) {
    var size = geometry || documentGeometry();
    var widthBucket = Math.max(1, Math.round(size.width / 100));
    var heightBucket = Math.max(1, Math.round(size.height / 250));
    return cleanToken([
      context.theme_id || 'theme',
      context.template_name || context.page_type || 'page',
      context.template_suffix || 'default',
      deviceCategory(),
      widthBucket,
      heightBucket
    ].join(':'), 180);
  }

  function heatmapFrame(extra) {
    var geometry = documentGeometry();
    return Object.assign({
      layout_key: layoutKey(geometry),
      grid_columns: HEATMAP_COLUMNS,
      grid_rows: HEATMAP_ROWS,
      document_width: geometry.width,
      document_height: geometry.height
    }, extra || {});
  }

  function heatmapTargetKind(target) {
    if (!target || !target.closest) return 'surface';
    var control = target.closest('a[href], button, summary, img, video');
    if (!control) return 'surface';
    if (control.matches('a[href]')) return 'link';
    if (control.matches('button')) return 'button';
    if (control.matches('summary')) return 'summary';
    if (control.matches('img')) return 'image';
    if (control.matches('video')) return 'video';
    return 'surface';
  }

  function isPreviewTraffic() {
    if (context.design_mode) return true;
    if (String(context.theme_id || '') === '202765664590') return true;
    try {
      var params = new URLSearchParams(window.location.search);
      return params.has('preview_theme_id') || params.has('preview_key') || params.has('kfa_intelligence_debug');
    } catch (error) {
      return false;
    }
  }

  function basePayload() {
    return {
      schema_version: SCHEMA_VERSION,
      layer_version: VERSION,
      theme_id: String(context.theme_id || ''),
      occurred_at: new Date().toISOString(),
      page_path: window.location.pathname,
      page_type: context.page_type || '',
      template_name: context.template_name || '',
      template_suffix: context.template_suffix || '',
      language: context.language || document.documentElement.lang || '',
      device_category: deviceCategory(),
      viewport_width: Math.round(window.innerWidth || 0),
      viewport_height: Math.round(window.innerHeight || 0),
      kfa_line: context.line || '',
      kfa_world: context.world || '',
      kfa_exhibition: context.exhibition || '',
      collection_id: context.collection_id ? String(context.collection_id) : '',
      collection_handle: context.collection_handle || '',
      product_id: context.product_id ? String(context.product_id) : '',
      product_handle: context.product_handle || '',
      internal_traffic: isPreviewTraffic(),
      test_run_id: debugRunId,
      campaign: campaignContext()
    };
  }

  function mergePayload(extra) {
    return Object.assign(basePayload(), extra || {});
  }

  function analyticsReady() {
    return Boolean(
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    );
  }

  function flushQueue() {
    if (!analyticsReady()) {
      if (publishRetryTimer || publishAttempts >= maxPublishAttempts) return;
      publishAttempts += 1;
      publishRetryTimer = window.setTimeout(function () {
        publishRetryTimer = null;
        flushQueue();
      }, 250);
      return;
    }

    publishAttempts = 0;
    var pending = queue.slice();
    queue.length = 0;
    var publishItem = function (item) {
      try {
        Promise.resolve(window.Shopify.analytics.publish(item.name, item.payload)).catch(function () {});
      } catch (error) {}
    };

    if (pending.length <= 3) {
      pending.forEach(publishItem);
      return;
    }

    pending.forEach(function (item, index) {
      window.setTimeout(function () {
        publishItem(item);
      }, index * 120);
    });
  }

  function publish(name, extra) {
    var consent = analyticsConsentState();
    var transportEventId = 'kfa-web-' + randomHex(16);
    var item = {
      name: NAMESPACE + name,
      payload: mergePayload(Object.assign({}, extra || {}, {
        transport_event_id: transportEventId,
        transport_episode_id: consent === true ? transportEpisodeId() : ''
      }))
    };

    queue.push(item);
    flushQueue();
    enqueueDirect(item);
  }

  function publishOnce(key, name, extra) {
    if (fired.has(key)) return;
    fired.add(key);
    publish(name, extra);
  }

  function pathFromLink(link) {
    if (!link) return '';
    try {
      return new URL(link.href, window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  }

  function productHandleFromPath(path) {
    var match = String(path || '').match(/^\/products\/([^/?#]+)/);
    return match ? cleanToken(match[1], 100) : '';
  }

  function collectionHandleFromPath(path) {
    var match = String(path || '').match(/^\/collections\/([^/?#]+)/);
    return match ? cleanToken(match[1], 100) : '';
  }

  function deterministicNodeId(element, fallbackPrefix) {
    if (!element) return '';
    if (element.dataset && element.dataset.kfaNodeId) return element.dataset.kfaNodeId;

    var productHost = element.closest && element.closest('[data-product-id]');
    var link = element.matches && element.matches('a') ? element : element.querySelector && element.querySelector('a[href]');
    var path = pathFromLink(link);
    var productId = productHost && productHost.getAttribute('data-product-id');
    var summary = element.matches && element.matches('details') ? element.querySelector('summary') : null;
    var semanticLabel = summary ? summary.textContent : '';
    var raw = productId || productHandleFromPath(path) || collectionHandleFromPath(path) || path;
    var fallback = raw || element.id || semanticLabel || element.getAttribute('aria-label') || element.className || 'unknown';
    var id = cleanToken((fallbackPrefix || 'element') + ':' + fallback, 140);

    if (element.dataset) element.dataset.kfaNodeId = id;
    return id;
  }

  function classifyLink(link) {
    var path = pathFromLink(link);
    var productHandle = productHandleFromPath(path);
    var collectionHandle = collectionHandleFromPath(path);
    return {
      destination_path: path,
      destination_product_handle: productHandle,
      destination_collection_handle: collectionHandle
    };
  }

  function markSemanticNodes() {
    document.querySelectorAll('section.okd-portal .portal__left').forEach(function (node) {
      node.dataset.kfaNodeId = 'portal:atelir';
      node.dataset.kfaIntent = 'portal_choice';
      node.dataset.kfaValue = 'atelir';
    });

    document.querySelectorAll('section.okd-portal .portal__right').forEach(function (node) {
      node.dataset.kfaNodeId = 'portal:xposure';
      node.dataset.kfaIntent = 'portal_choice';
      node.dataset.kfaValue = 'xposure';
    });

    document.querySelectorAll('product-card[data-product-id]').forEach(function (node) {
      node.dataset.kfaNodeId = 'artwork:' + cleanToken(node.dataset.productId, 100);
      node.dataset.kfaIntent = 'artwork';
    });

    document.querySelectorAll('.tile').forEach(function (node) {
      var link = node.querySelector('a[href*="/products/"]');
      if (!link) return;
      node.dataset.kfaNodeId = 'artwork:' + productHandleFromPath(pathFromLink(link));
      node.dataset.kfaIntent = 'artwork';
    });

    document.querySelectorAll('.footer-tabs__nav [data-tab]').forEach(function (node) {
      node.dataset.kfaNodeId = 'footer-tab:' + cleanToken(node.getAttribute('data-tab'), 50);
      node.dataset.kfaIntent = node.hasAttribute('data-kfa-portal') ? 'line_switch' : 'depth_open';
    });
  }

  function observeArtworkImpressions() {
    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var target = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
          if (impressionTimers.has(target)) return;
          var timer = window.setTimeout(function () {
            var link = target.querySelector('a[href*="/products/"]');
            var path = pathFromLink(link);
            var nodeId = deterministicNodeId(target, 'artwork');
            publishOnce('impression:' + nodeId, 'artwork_impression', Object.assign({
              node_id: nodeId,
              impression_ratio: Number(entry.intersectionRatio.toFixed(2)),
              destination_product_handle: productHandleFromPath(path)
            }, classifyLink(link)));
            observer.unobserve(target);
            impressionTimers.delete(target);
          }, 650);
          impressionTimers.set(target, timer);
        } else if (impressionTimers.has(target)) {
          window.clearTimeout(impressionTimers.get(target));
          impressionTimers.delete(target);
        }
      });
    }, { threshold: [0, 0.55, 1] });

    document.querySelectorAll('[data-kfa-intent="artwork"]').forEach(function (node) {
      if (observedElements.has(node)) return;
      observedElements.add(node);
      observer.observe(node);
    });
  }

  function recordClickPoint(event) {
    if (!heatmapPageAllowed() || sensitiveTarget(event.target)) return;

    if (debugMode()) {
      debugClickCount += 1;
      persistDebugCounters();
      updateDebugPanel();
    }

    var coordinates = heatmapCoordinates(event);
    var control = event.target && event.target.closest
      ? event.target.closest('a[href], button, summary, img, video')
      : null;
    var nodeId = control ? deterministicNodeId(control, 'click') : 'surface:page';

    publish('click_point', heatmapFrame(Object.assign({}, coordinates, {
      node_id: nodeId,
      target_kind: heatmapTargetKind(event.target),
      pointer_type: cleanToken(event.pointerType || lastPointerType || 'mouse', 20) || 'mouse'
    })));
  }

  function flushPointerDensity() {
    if (!pointerCells.size) return;

    var cells = [];
    var sampleCount = 0;
    pointerCells.forEach(function (count, key) {
      if (cells.length >= 512) return;
      var parts = key.split(':');
      var cellCount = Math.min(500, Number(count) || 0);
      if (!cellCount) return;
      cells.push([Number(parts[0]), Number(parts[1]), cellCount]);
      sampleCount += cellCount;
    });
    pointerCells.clear();

    if (!cells.length) return;
    if (debugMode()) {
      // The diagnostic counter represents samples that have actually entered
      // a transport batch, not transient pointer observations still waiting
      // in memory. This keeps local and server truth comparable on WebKit.
      debugPointerCount += sampleCount;
      persistDebugCounters();
      updateDebugPanel();
    }
    publish('pointer_density', heatmapFrame({
      pointer_type: 'mouse',
      sample_interval_ms: POINTER_SAMPLE_INTERVAL,
      sample_count: sampleCount,
      cells: cells
    }));
  }

  function observeHeatmap() {
    if (!heatmapPageAllowed()) return;

    document.addEventListener('pointerdown', function (event) {
      var pointerType = cleanToken(event.pointerType || '', 20);
      if (pointerType === 'mouse' || pointerType === 'touch' || pointerType === 'pen') {
        lastPointerType = pointerType;
      }
      // Pixel 0.2.4 delivers pointer and click envelopes concurrently. Flush
      // the pending mouse batch before navigation destroys Shopify's sandbox.
      if (pointerCells.size) flushPointerDensity();
    }, { passive: true });

    document.addEventListener('pointermove', function (event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (sensitiveTarget(event.target)) return;

      var now = Date.now();
      if (now - lastPointerSampleAt < POINTER_SAMPLE_INTERVAL) return;
      lastPointerSampleAt = now;

      var coordinates = heatmapCoordinates(event);
      var key = coordinates.grid_col + ':' + coordinates.grid_row;
      pointerCells.set(key, Math.min(500, (pointerCells.get(key) || 0) + 1));
    }, { passive: true });

    heatmapFlushTimer = window.setInterval(flushPointerDensity, HEATMAP_FLUSH_INTERVAL);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flushPointerDensity();
    });
    window.addEventListener('pagehide', flushPointerDensity);
  }

  function handleClick(event) {
    if (event.type === 'click' && lastMousePointerDownTarget && Date.now() - lastMousePointerDownAt < 1200) {
      var sameTarget = event.target === lastMousePointerDownTarget;
      var nestedTarget = event.target && event.target.contains && event.target.contains(lastMousePointerDownTarget);
      var parentTarget = lastMousePointerDownTarget.contains && lastMousePointerDownTarget.contains(event.target);
      if (sameTarget || nestedTarget || parentTarget) return;
    }

    recordClickPoint(event);

    var control = event.target && event.target.closest
      ? event.target.closest('a[href], button, summary')
      : null;
    if (!control) return;

    var portal = control.closest('[data-kfa-intent="portal_choice"]');
    if (portal) {
      publish('portal_choice', Object.assign({
        node_id: portal.dataset.kfaNodeId,
        choice: portal.dataset.kfaValue
      }, classifyLink(control.matches('a') ? control : portal.querySelector('a[href]'))));
      return;
    }

    var artwork = control.closest('[data-kfa-intent="artwork"]');
    if (artwork) {
      var artworkLink = control.matches('a') ? control : artwork.querySelector('a[href*="/products/"]');
      publish('artwork_select', Object.assign({
        node_id: deterministicNodeId(artwork, 'artwork')
      }, classifyLink(artworkLink)));
      return;
    }

    var lineSwitch = control.closest('[data-kfa-intent="line_switch"]');
    if (lineSwitch) {
      publish('line_switch', Object.assign({
        node_id: deterministicNodeId(lineSwitch, 'line-switch'),
        destination_line: cleanToken(lineSwitch.getAttribute('data-kfa-portal'), 30)
      }, classifyLink(lineSwitch.matches('a') ? lineSwitch : null)));
      return;
    }

    var href = control.matches('a') ? String(control.getAttribute('href') || '') : '';
    if (/search|#search/i.test(href) || control.closest('.search-action, search-modal')) {
      publish('search_open', { node_id: deterministicNodeId(control, 'search') });
      return;
    }

    if (/contact|consult|advice|beratung|mailto:/i.test(href)) {
      publish('advice_click', Object.assign({
        node_id: deterministicNodeId(control, 'advice')
      }, classifyLink(control)));
      return;
    }

    if (control.matches('summary') || control.closest('details')) {
      var details = control.closest('details');
      publish('depth_open', {
        node_id: deterministicNodeId(details || control, 'depth'),
        depth_type: 'details'
      });
      return;
    }

    var destination = classifyLink(control.matches('a') ? control : null);
    if (destination.destination_collection_handle) {
      publish('world_select', Object.assign({
        node_id: deterministicNodeId(control, 'collection')
      }, destination));
    }
  }

  function handleMousePointerDown(event) {
    var pointerType = cleanToken(event.pointerType || '', 20);
    if (pointerType && pointerType !== 'mouse') return;
    if (Number(event.button) !== 0) return;

    lastMousePointerDownTarget = event.target;
    lastMousePointerDownAt = Date.now();
    handleClick(event);
  }

  function handleChange(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var facets = target.closest('facets-form, .facets, [data-testid="facets"]');
    if (!facets) return;

    var isSort = /sort/i.test(target.name || target.id || '');
    publish(isSort ? 'sort_use' : 'filter_use', {
      node_id: deterministicNodeId(target, isSort ? 'sort' : 'filter'),
      control_name: cleanToken(target.name || target.id || '', 80)
    });
  }

  function observeScrollDepth() {
    var thresholds = [25, 50, 75, 90];
    var ticking = false;

    function measure() {
      ticking = false;
      var root = document.documentElement;
      if (root.classList.contains('kfa-auto-footer-glide-active')) return;
      var scrollable = Math.max(1, root.scrollHeight - window.innerHeight);
      var depth = Math.round((window.scrollY / scrollable) * 100);
      thresholds.forEach(function (threshold) {
        if (depth >= threshold) {
          publishOnce('scroll:' + threshold, 'scroll_depth', {
            threshold_percent: threshold
          });
        }
      });
    }

    window.addEventListener('scroll', function () {
      lastActivityAt = Date.now();
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }, { passive: true });

    measure();
  }

  function observeEngagement() {
    ['pointerdown', 'keydown', 'touchstart'].forEach(function (name) {
      window.addEventListener(name, function () {
        lastActivityAt = Date.now();
      }, { passive: true });
    });

    window.setInterval(function () {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityAt > 15000) return;

      activeSeconds += 1;
      if (activeSeconds === 30 || activeSeconds === 90) {
        publish('engaged_time', { active_seconds: activeSeconds });
      }
    }, 1000);
  }

  function initialize() {
    initializeDebugPanel();
    hydrateDirectPending();
    drainDirectQueue();
    markSemanticNodes();
    observeArtworkImpressions();
    observeScrollDepth();
    observeEngagement();
    observeHeatmap();

    document.addEventListener('pointerdown', handleMousePointerDown, true);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('change', handleChange, true);

    publish('page_context', {
      node_id: 'page:' + cleanToken(window.location.pathname || '/', 120)
    });

    window.addEventListener('load', flushQueue, { once: true });
    window.addEventListener('pageshow', flushQueue);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') flushQueue();
    });

    document.addEventListener('shopify:section:load', function () {
      markSemanticNodes();
      observeArtworkImpressions();
    });

    window.KFAIntelligence = Object.freeze({
      version: VERSION,
      schemaVersion: SCHEMA_VERSION,
      publish: publish
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
